import { useEffect, useState } from "react";
import { Layout } from "../components/Layout";
import { api, eur, API } from "../lib/api";
import { toast } from "sonner";
import { Plus, X, Download, FileText, TrendingUp } from "lucide-react";

const STATUS = {
  pendente: { label: "Pendente", cls: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
  faturada: { label: "Faturada", cls: "bg-blue-500/15 text-blue-300 border-blue-500/30" },
  recebida: { label: "Recebida", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" },
};
const EMPTY = { deal_description: "", client_nif: "", amount: "", status: "pendente" };

export default function Financeiro() {
  const [invoices, setInvoices] = useState([]);
  const [rappel, setRappel] = useState(null);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const load = () => {
    api.get("/invoices").then((r) => setInvoices(r.data)).catch(() => {});
    api.get("/invoices/rappel").then((r) => setRappel(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/invoices", { ...form, amount: parseFloat(form.amount) || 0 });
      toast.success("Requisição de fatura criada");
      setModal(false); setForm(EMPTY); load();
    } catch { toast.error("Erro ao criar"); }
  };

  const changeStatus = async (id, status) => { await api.patch(`/invoices/${id}`, { status }); load(); };

  const exportCsv = () => {
    const token = localStorage.getItem("almar_token");
    window.open(`${API}/invoices/export?auth=${token}`, "_blank");
  };

  const nextThreshold = rappel?.next_tier?.threshold || (rappel?.current_tier?.threshold || 0);
  const progress = rappel && rappel.next_tier
    ? Math.min(100, ((rappel.total_faturado - rappel.current_tier.threshold) / (rappel.next_tier.threshold - rappel.current_tier.threshold)) * 100)
    : 100;

  return (
    <Layout title="Financeiro">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <p className="text-sm text-slate-400">Requisição de faturas, rappel e exportação</p>
        <div className="flex gap-2">
          <button onClick={exportCsv} data-testid="export-csv-btn"
            className="flex items-center gap-2 bg-[#152A3E] hover:bg-[#1A344D] text-slate-200 border border-[#1E3A56] hover:border-[#C9A84C]/50 px-4 py-2.5 rounded-lg transition-all">
            <Download size={17} /> Exportar CSV
          </button>
          <button onClick={() => setModal(true)} data-testid="new-invoice-btn"
            className="flex items-center gap-2 bg-[#C9A84C] hover:bg-[#D9B85C] text-[#0D1B2A] font-semibold px-4 py-2.5 rounded-lg transition-all hover:scale-[1.02]">
            <Plus size={18} /> Requisitar Fatura
          </button>
        </div>
      </div>

      {rappel && (
        <div className="card-almar p-5 mb-6" data-testid="rappel-card">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display text-lg text-slate-100 flex items-center gap-2"><TrendingUp size={18} className="text-[#C9A84C]" /> Rappel de Faturação</h3>
            <span className="text-sm px-3 py-1 rounded-full bg-[#C9A84C]/15 text-[#E0C574] border border-[#C9A84C]/30">
              {rappel.current_tier.name} · {rappel.current_tier.rate}%
            </span>
          </div>
          <div className="flex items-baseline gap-2 mb-2">
            <span className="text-2xl font-bold text-slate-100">{eur(rappel.total_faturado)}</span>
            <span className="text-xs text-slate-500">faturado</span>
          </div>
          <div className="h-3 rounded-full bg-[#0D1B2A] border border-[#1E3A56] overflow-hidden">
            <div className="h-full rounded-full transition-all" style={{ width: `${progress}%`, background: "linear-gradient(90deg,#9B7B2B,#C9A84C,#E0C574)" }} />
          </div>
          <p className="text-xs text-slate-400 mt-2">
            {rappel.next_tier
              ? `Faltam ${eur(rappel.next_tier.threshold - rappel.total_faturado)} para ${rappel.next_tier.name} (${rappel.next_tier.rate}%)`
              : "Atingiu o escalão máximo de rappel 🎉"}
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-4">
            {rappel.tiers.map((t) => (
              <div key={t.name} className={`p-3 rounded-lg border text-center ${t.name === rappel.current_tier.name ? "bg-[#C9A84C]/15 border-[#C9A84C]/40" : "bg-[#0D1B2A] border-[#1E3A56]"}`}>
                <div className="text-xs text-slate-400">{t.name}</div>
                <div className="text-lg font-bold text-[#E0C574]">{t.rate}%</div>
                <div className="text-[10px] text-slate-500">≥ {eur(t.threshold)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="card-almar p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#1E3A56] text-left text-xs uppercase tracking-wider text-slate-400">
                <th className="px-4 py-3">Data</th>
                <th className="px-4 py-3">Negócio</th>
                <th className="px-4 py-3">NIF</th>
                <th className="px-4 py-3 text-right">Montante</th>
                <th className="px-4 py-3">Estado</th>
              </tr>
            </thead>
            <tbody data-testid="invoices-table">
              {invoices.map((i) => (
                <tr key={i.id} className="border-b border-[#1E3A56]/50 hover:bg-[#1A344D]/40">
                  <td className="px-4 py-3 text-slate-400">{(i.created_at || "").slice(0, 10)}</td>
                  <td className="px-4 py-3 text-slate-200">{i.deal_description}</td>
                  <td className="px-4 py-3 text-slate-400 font-mono text-xs">{i.client_nif}</td>
                  <td className="px-4 py-3 text-right text-[#E0C574] font-semibold">{eur(i.amount)}</td>
                  <td className="px-4 py-3">
                    <select value={i.status} onChange={(e) => changeStatus(i.id, e.target.value)}
                      className={`text-xs px-2 py-1 rounded-full border bg-transparent ${STATUS[i.status]?.cls}`}>
                      {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k} className="bg-[#0D1B2A]">{v.label}</option>)}
                    </select>
                  </td>
                </tr>
              ))}
              {invoices.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-500">Sem requisições de fatura.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => setModal(false)}>
          <div className="card-almar w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} data-testid="invoice-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-xl text-[#C9A84C] flex items-center gap-2"><FileText size={20} /> Requisitar Fatura</h3>
              <button onClick={() => setModal(false)}><X size={20} className="text-slate-400" /></button>
            </div>
            <form onSubmit={save} className="space-y-3">
              <input className="input-almar" placeholder="Descrição do negócio" required data-testid="invoice-desc-input"
                value={form.deal_description} onChange={(e) => setForm({ ...form, deal_description: e.target.value })} />
              <input className="input-almar" placeholder="NIF do cliente" required data-testid="invoice-nif-input"
                value={form.client_nif} onChange={(e) => setForm({ ...form, client_nif: e.target.value })} />
              <input className="input-almar" type="number" placeholder="Montante (€)" required data-testid="invoice-amount-input"
                value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
              <select className="input-almar" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
              <button type="submit" data-testid="invoice-save-btn" className="w-full bg-[#C9A84C] hover:bg-[#D9B85C] text-[#0D1B2A] font-semibold py-2.5 rounded-lg transition-all">Criar Requisição</button>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}
